import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { type AssetHolderSettlementDetailsDto } from '../../api-schema-types/asset-holdings/AssetHolderSettlementDomainDtos'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'
import { assetHolderApiMapper } from './assetHolderApiMapper'
import {
  type AssetHolderProfileUpdateSummary,
  type AssetHolderSummary,
  type EmailNotificationsGroupKeys, type EmailNotificationsStatus
} from '../../api-schema-types/asset-holdings/AssetHolderDomainDtos'
import { type AssetHolderUpdateDto } from '../../api-schema-types/asset-holdings/AssetHolderCrudDtos'
import {
  type AssetHolderEmailGetGroupKeysPayload, type AssetHolderEmailJobPayload,
  type AssetHoldersSearchRequest, type UpdateAssetHolderEmailAddressPayload
} from '../../api-schema-types/asset-holdings/AssetHolderPayloadDto'
// import { type AssetHolderSettlementDto } from '../../api-schema-types/asset-holdings/PortfolioSettlementDomainDtos'

export interface AssetHolderIdProps {
  assetHolderId: number | null
}

export const assetHolderApi = createApi({
  reducerPath: 'assetHolderApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/asset-holdings`,
    prepareHeaders: (headers: Headers, { getState }) => {
      const authToken = ApiAuthStrategies.getBearerTokenHeader()
      headers.set('authorization', authToken)
      return headers
    }
  }),

  tagTypes: ['AssetHolders', 'AssetHolder'],

  endpoints: (builder) => ({
    getAssetHolder: builder.query({
      query: ({ assetHolderId }: AssetHolderIdProps) => ({
        url: `/asset-holders/${assetHolderId}`,
        method: 'GET'
      }),
      providesTags: ['AssetHolder'],
      transformResponse: (response: AssetHolderUpdateDto, meta, arg) => assetHolderApiMapper().assetHolderDetailsMapper(response, meta, arg)
    }),

    search: builder.query({
      query: (search: AssetHoldersSearchRequest) => ({
        url: '/asset-holders-search',
        method: 'GET',
        params: search
      }),
      providesTags: ['AssetHolders'],
      transformResponse: (response: AssetHolderSummary[], meta, arg) => assetHolderApiMapper().assetHolderSummaryMapper(response, meta, arg)
    }),

    update: builder.mutation<AssetHolderUpdateDto, Partial<AssetHolderUpdateDto>>({
      query: ({ ...patch }) => ({
        url: `/asset-holders/${patch.assetHolderId}`,
        method: 'put',
        body: patch
      }),
      invalidatesTags: ['AssetHolder']
    }),
    removeUsCitizen: builder.mutation<undefined, Partial<number>>({
      query: (assetHolderId: number) => ({
        url: `/asset-holders/individual/${assetHolderId}/citizen-status`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AssetHolder', 'AssetHolders']
    }),
    disableMember: builder.mutation({
      query: ({ ...patch }) => ({
        url: 'asset-holders/individual/disable',
        method: 'DELETE',
        body: patch
      }),
      invalidatesTags: ['AssetHolder', 'AssetHolders']
    }),
    deleteLegacyRole: builder.mutation({
      query: ({ ...patch }) => ({
        url: 'asset-holders/individual/delete-role',
        method: 'DELETE',
        body: patch
      }),
      invalidatesTags: ['AssetHolder']
    }),
    addLegacyRole: builder.mutation({
      query: ({ ...patch }) => ({
        url: 'asset-holders/individual/add-role',
        method: 'POST',
        body: patch
      }),
      invalidatesTags: ['AssetHolder']
    }),
    toggleVIPAccess: builder.mutation({
      query: ({ ...patch }) => ({
        url: 'asset-holders/individual/toggle-vip-access',
        method: 'PUT',
        body: patch
      }),
      invalidatesTags: ['AssetHolder', 'AssetHolders']
    }),
    disableTwoFA: builder.mutation({
      query: ({ ...patch }) => ({
        url: 'asset-holders/individual/disable-2fa',
        method: 'DELETE',
        body: patch
      }),
      invalidatesTags: ['AssetHolder']
    }),

    // ==============================================================================================================
    // KYC
    // ==============================================================================================================

    addInstitutionKycResult: builder.mutation({
      query: ({ ...patch }) => ({
        url: 'asset-holders/individual/add-institution-kyc-result',
        method: 'POST',
        body: patch
      }),
      invalidatesTags: ['AssetHolder', 'AssetHolders']
    }),
    addIndividualKycResult: builder.mutation({
      query: ({ ...patch }) => ({
        url: 'asset-holders/individual/add-individual-kyc-result',
        method: 'POST',
        body: patch
      }),
      invalidatesTags: ['AssetHolder', 'AssetHolders']
    }),
    resetLegacyKycResult: builder.mutation({
      query: ({ ...patch }) => ({
        url: 'asset-holders/individual/reset-kyc',
        method: 'DELETE',
        body: patch
      }),
      invalidatesTags: ['AssetHolder', 'AssetHolders']
    }),

    // ==============================================================================================================

    updateEmailAddress: builder.mutation({
      query: (payload: UpdateAssetHolderEmailAddressPayload) => ({
        url: '/asset-holders/admin-updates/update-email',
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: ['AssetHolder']
    }),

    getProfileUpdates: builder.query({
      query: (assetHolderId: number) => ({
        url: `/asset-holders/${assetHolderId}/profile-updates`,
        method: 'GET'
      }),
      providesTags: ['AssetHolder'],
      transformResponse: (response: AssetHolderProfileUpdateSummary[], meta, arg) => response
    }),

    // ================================================================================================================

    /// asset-holdings/email-notifications/mails/group_keys
    mailGroupKeys: builder.query({
      query: (reportType: AssetHolderEmailGetGroupKeysPayload) => ({
        url: '/email-notifications/mails/group_keys',
        method: 'GET',
        params: reportType
      }),
      transformResponse: (response: EmailNotificationsGroupKeys, meta, arg) => assetHolderApiMapper().emailNotificationsGroupKeysMapper(response, meta, arg)
    }),
    mailStatus: builder.query({
      query: (reportType: AssetHolderEmailJobPayload) => ({
        url: '/email-notifications/mails/status',
        method: 'GET',
        params: reportType
      }),
      transformResponse: (response: EmailNotificationsStatus, meta, arg) => assetHolderApiMapper().emailNotificationsStatusMapper(response, meta, arg)
    }),
    /// asset-holdings/email-notifications/mails/create
    createMail: builder.mutation({
      query: ({ ...patch }) => ({
        url: '/email-notifications/mails/create',
        method: 'POST',
        body: patch
      })
    }),
    /// /asset-holdings/email-notifications/mails/send
    sendMail: builder.mutation({
      query: ({ ...patch }) => ({
        url: '/email-notifications/mails/send',
        method: 'POST',
        body: patch
      })
    }),

    // ================================================================================================================

    getCurrentSettlement: builder.query({
      query: ({ assetHolderId }: AssetHolderIdProps) => ({
        url: `/asset-holders/${assetHolderId}/settlements`
      }),
      transformResponse: (response: AssetHolderSettlementDetailsDto[], meta, arg: AssetHolderIdProps) => response
    })

  })
})

export const useGetAssetHolderQuery = assetHolderApi.endpoints.getAssetHolder.useQuery
export const useSearchForAssetHolderQuery = assetHolderApi.endpoints.search.useQuery
export const useUpdateAssetHolderMutation = assetHolderApi.endpoints.update.useMutation
export const useAssetHolderApiRemoveCitizen = assetHolderApi.endpoints.removeUsCitizen.useMutation
export const useLegacyApiDisableMemberMutation = assetHolderApi.endpoints.disableMember.useMutation
export const useLegacyApiDeleteLegacyRoleMutation = assetHolderApi.endpoints.deleteLegacyRole.useMutation
export const useLegacyApiAddLegacyRoleMutation = assetHolderApi.endpoints.addLegacyRole.useMutation
export const useLegacyApiAddInstitutionKycResultMutation = assetHolderApi.endpoints.addInstitutionKycResult.useMutation
export const useLegacyApiAddIndividualKycResultMutation = assetHolderApi.endpoints.addIndividualKycResult.useMutation
export const useLegacyApiResetLegacyKycResultMutation = assetHolderApi.endpoints.resetLegacyKycResult.useMutation
export const useLegacyApiToggleVIPAccessMutation = assetHolderApi.endpoints.toggleVIPAccess.useMutation
export const useLegacyApiDisableTwoFAMutation = assetHolderApi.endpoints.disableTwoFA.useMutation

export const useGetMailGroupKeysQuery = assetHolderApi.endpoints.mailGroupKeys.useQuery
export const useGetMailStatusQuery = assetHolderApi.endpoints.mailStatus.useQuery

export const useCreateMailMutation = assetHolderApi.endpoints.createMail.useMutation
export const useSendMailMutation = assetHolderApi.endpoints.sendMail.useMutation

export const useUpdateEmailMutation = assetHolderApi.endpoints.updateEmailAddress.useMutation
export const useGetProfileUpdatesQuery = assetHolderApi.endpoints.getProfileUpdates.useQuery

export const useGetCurrentSettlementQuery = assetHolderApi.endpoints?.getCurrentSettlement.useQuery
